/*
 * Zoom Gallery — scoped to [data-zoom-root].
 * Depends on createFocusTrap() from assets/focus-trap.js (loaded first by consuming section).
 * Hooks: [data-zoom-gallery], [data-zoom-scroll], [data-zoom-bar], [data-zoom-trigger][data-zoom-trigger-index],
 *        [data-zoom-close], [data-zoom-images], [data-zoom-image][data-zoom-image-index],
 *        [data-zoom-thumbnails], [data-zoom-thumb][data-zoom-thumb-index].
 * Scroll lock: html scroll-lock attribute per application.css base rule (set/removed on open/close).
 * Open state: data-zoom-open on <html>, so the sticky add-to-bag bar can react — its own observer
 * sees the buy area as still on screen and would otherwise hold it inert.
 */

(function () {
  const SCROLL_GUARD = 700; // ms — covers smooth-scroll so IO does not re-fire mid-scroll

  class ZoomGallery {
    constructor(root) {
      this.root        = root;
      this.overlay     = root.querySelector('[data-zoom-gallery]');
      // The overlay positions; this scrolls.
      this.scroller    = root.querySelector('[data-zoom-scroll]');
      this.bar         = root.querySelector('[data-zoom-bar]');
      this.closeBtn    = root.querySelector('[data-zoom-close]');
      this.imagesDiv   = root.querySelector('[data-zoom-images]');
      this.images      = [...root.querySelectorAll('[data-zoom-image]')];
      this.thumbs      = [...root.querySelectorAll('[data-zoom-thumb]')];
      this.triggers    = [...root.querySelectorAll('[data-zoom-trigger]')];
      this.busy        = false;
      this.scrollTimer = null;
      this.returnFocus = null;
      this.io          = null;

      if (!this.overlay || !this.scroller || !this.imagesDiv || !this.images.length) return;

      /* global createFocusTrap */
      this.focusTrap = typeof createFocusTrap === 'function' ? createFocusTrap(this.overlay) : null;
      this.bindEvents();
    }

    open(startIndex, returnFocusEl) {
      const idx = Number.isFinite(startIndex) ? startIndex : 0;
      this.returnFocus = returnFocusEl ?? document.activeElement;
      this.overlay.classList.remove('hidden');
      this.overlay.removeAttribute('aria-hidden'); // must precede focusTrap.activate (see R4)
      document.documentElement.setAttribute('data-scroll-lock', '');
      document.documentElement.setAttribute('data-zoom-open', '');
      this.io = this.observeScroll();
      this.focusTrap?.activate();
      this.closeBtn?.focus();
      this.activateThumb(idx);
      if (idx > 0) {
        const target = this.images[idx];
        if (target) this.scroller.scrollTop = target.offsetTop;
      }
    }

    close() {
      this.io?.disconnect();
      this.io = null;
      this.focusTrap?.deactivate();
      this.overlay.classList.add('hidden');
      this.overlay.setAttribute('aria-hidden', 'true');
      this.scroller.scrollTop = 0; // reset for next open
      document.documentElement.removeAttribute('data-scroll-lock');
      document.documentElement.removeAttribute('data-zoom-open');
      this.activateThumb(0);
      this.returnFocus?.focus();
    }

    activateThumb(idx) {
      this.thumbs.forEach((t, i) => t.setAttribute('aria-pressed', String(i === idx)));
    }

    scrollToImage(idx) {
      const target = this.images[idx];
      if (!target) return;
      this.activateThumb(idx); // T=0 visual feedback — active thumb before scroll starts
      this.busy = true;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced) {
        this.scroller.scrollTop = target.offsetTop;
      } else {
        this.scroller.scrollTo({ top: target.offsetTop, behavior: 'smooth' });
      }
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => { this.busy = false; }, reduced ? 50 : SCROLL_GUARD);
    }

    observeScroll() {
      if (!('IntersectionObserver' in window)) return null;
      const io = new IntersectionObserver(
        (entries) => {
          if (this.busy) return;
          const top = entries
            .filter((e) => e.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
          if (!top) return;
          const idx = parseInt(top.target.dataset.zoomImageIndex, 10);
          if (Number.isFinite(idx)) this.activateThumb(idx);
        },
        // Ratio is relative to the IMAGE, far taller than the scroller, so it peaks well
        // under half. The callback picks the highest-ratio entry, so this only sets how
        // early tracking starts.
        { root: this.scroller, threshold: 0.15 }
      );
      this.images.forEach((img) => io.observe(img));
      return io;
    }

    bindEvents() {
      this.triggers.forEach((t) => {
        t.addEventListener('click', () => {
          this.open(parseInt(t.dataset.zoomTriggerIndex, 10), t);
        });
      });

      this.closeBtn?.addEventListener('click', () => this.close());

      this.overlay.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { e.preventDefault(); this.close(); }
      });

      this.thumbs.forEach((t) => {
        t.addEventListener('click', () => {
          const idx = parseInt(t.dataset.zoomThumbIndex, 10);
          if (Number.isFinite(idx)) this.scrollToImage(idx);
        });
      });
    }
  }

  function init(container) {
    const selector = '[data-zoom-root]:not([data-zoom-init])';
    const roots = container.matches?.(selector) ? [container] : container.querySelectorAll(selector);
    roots.forEach((root) => {
      root.setAttribute('data-zoom-init', '');
      new ZoomGallery(root);
    });
  }

  init(document);
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
